/**
 * useFlowDraftSave — the per-tracker save of a Flows-tab draft: Voluum's
 * per-item calls, Binom's full-flow PUT, and the same per-item calls at
 * /tres-commas/flows for Tres Commas (a Voluum-API clone). Every tracker's
 * mutation hooks are created (rules of hooks); only the chosen saver sends.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useMemo } from 'react';

import {
  useToggleFlowPathStatus,
  useTresCommasToggleFlowPathStatus,
  useTresCommasUpdateFlowItemWeights,
  useTresCommasUpdateFlowPathWeights,
  useUpdateBinomFlow,
  useUpdateFlowItemWeights,
  useUpdateFlowPathWeights,
} from '@/hooks';
import type { CampaignProvider } from '@/hooks/voluum/useVoluum';
import { saveFlowDraftFor } from '@/features/flows/detail/utils/flow-draft-save.util';
import type { DraftPath } from '@/features/flows/detail/utils/flowDraft.utils';

import { saveBinomFullFlow, type PathGroupRaw } from '../useFlowDraft.binom-save';
import { saveVoluumPerItem, type SaveVoluumPerItemParams } from '../useFlowDraft.voluum-save';

type PerItemMutations = Omit<SaveVoluumPerItemParams, 'flowId' | 'draftPaths'>;

export interface FlowDraftSaveParams {
  flowId: string;
  campaignId?: string;
  provider: CampaignProvider;
  flowName: string;
  pathGroupsRaw?: PathGroupRaw[];
}

function useVoluumMutations(): PerItemMutations {
  const togglePathMutation = useToggleFlowPathStatus();
  const updateItemWeightsMutation = useUpdateFlowItemWeights();
  const updatePathWeightsMutation = useUpdateFlowPathWeights();
  return useMemo(
    () => ({ togglePathMutation, updateItemWeightsMutation, updatePathWeightsMutation }),
    [togglePathMutation, updateItemWeightsMutation, updatePathWeightsMutation],
  );
}

function useTresCommasMutations(): PerItemMutations {
  const togglePathMutation = useTresCommasToggleFlowPathStatus();
  const updateItemWeightsMutation = useTresCommasUpdateFlowItemWeights();
  const updatePathWeightsMutation = useTresCommasUpdateFlowPathWeights();
  return useMemo(
    () => ({ togglePathMutation, updateItemWeightsMutation, updatePathWeightsMutation }),
    [togglePathMutation, updateItemWeightsMutation, updatePathWeightsMutation],
  );
}

/** Returns `save(draftPaths)` for the draft's tracker. */
export function useFlowDraftSave(params: FlowDraftSaveParams) {
  const { flowId, campaignId, provider, flowName, pathGroupsRaw } = params;
  const voluum = useVoluumMutations();
  const tresCommas = useTresCommasMutations();
  const binomMutation = useUpdateBinomFlow(flowId, campaignId);

  return useCallback(
    (draftPaths: DraftPath[]) =>
      saveFlowDraftFor(provider, {
        binom: () =>
          saveBinomFullFlow({ flowId, flowName, draftPaths, pathGroupsRaw, mutation: binomMutation }),
        voluum: () => saveVoluumPerItem({ flowId, draftPaths, ...voluum }),
        'tres-commas': () => saveVoluumPerItem({ flowId, draftPaths, ...tresCommas }),
      }),
    [provider, flowId, flowName, pathGroupsRaw, binomMutation, voluum, tresCommas],
  );
}
